import React from "react";
import { Link } from "react-router-dom";
import { Target, Clock, Users, ArrowRight, Share2 } from "lucide-react";
import { SITE_URL } from "../lib/publicSite";
import { API } from "../lib/api";

const CampaignThermometer = ({ campaign, compact = false }) => {
    const goal = Number(campaign.goal_amount || 0);
    const raised = Number(campaign.raised || 0);
    const hasGoal = goal > 0;
    const pct = hasGoal ? Math.min((raised / goal) * 100, 100) : 0;
    const donateLink = `/donate?campaign=${campaign.id}`;
    // Public campaign landing page (only if we have a slug — General Support
    // Fund + legacy rows without slug fall back to the donate page).
    const landingLink = campaign.slug ? `/c/${campaign.slug}` : donateLink;

    const handleShare = () => {
        const ogShareUrl = `${SITE_URL}/api/og/campaigns/${encodeURIComponent(campaign.slug || campaign.id)}`;
        const text = hasGoal
            ? `${campaign.name} · $${Math.round(raised).toLocaleString()} of $${Math.round(goal).toLocaleString()} raised — help us hit our goal!`
            : `${campaign.name} · $${Math.round(raised).toLocaleString()} raised so far — every gift helps approved needs.`;
        if (navigator.share) {
            navigator.share({ title: campaign.name, text, url: ogShareUrl }).catch(() => {});
        } else {
            window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(ogShareUrl)}&quote=${encodeURIComponent(text)}`, "_blank", "width=600,height=500");
        }
    };

    return (
        <div
            data-testid={`campaign-thermometer-${campaign.id}`}
            className={`bg-white border border-black/5 rounded-3xl overflow-hidden hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all flex flex-col ${
                compact ? "" : "md:flex-row"
            }`}
        >
            {campaign.image_url && (
                <Link
                    to={landingLink}
                    data-testid={`campaign-image-link-${campaign.id}`}
                    aria-label={`Open ${campaign.name} campaign page`}
                    className={`block bg-[#F4F1EA] flex-shrink-0 group ${compact ? "aspect-[4/3]" : "md:w-2/5 aspect-[4/3] md:aspect-auto"}`}
                >
                    <img
                        src={campaign.image_url}
                        alt={campaign.name}
                        loading="lazy"
                        className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                    />
                </Link>
            )}
            <div className={`flex-1 p-6 sm:p-8 ${compact ? "" : "md:p-10"} flex flex-col`}>
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                    <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#E53935] flex items-center gap-1.5">
                        <Target className="w-3.5 h-3.5" strokeWidth={2} />
                        Active campaign
                    </p>
                    {campaign.days_left !== null && (
                        <p className="text-xs uppercase tracking-wider text-[#4A5568] flex items-center gap-1.5" data-testid={`campaign-days-${campaign.id}`}>
                            <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                            {campaign.days_left === 0 ? "Ends today" : `${campaign.days_left} day${campaign.days_left === 1 ? "" : "s"} left`}
                        </p>
                    )}
                </div>
                <h3 className={`font-heading ${compact ? "text-xl" : "text-2xl md:text-3xl"} text-[#1C252E] font-medium leading-tight mb-3`}>
                    <Link
                        to={landingLink}
                        data-testid={`campaign-title-link-${campaign.id}`}
                        className="hover:text-[#E53935] transition-colors"
                    >
                        {campaign.name}
                    </Link>
                </h3>
                {campaign.description && (
                    <p className="text-sm text-[#4A5568] leading-relaxed mb-5 line-clamp-2">{campaign.description}</p>
                )}

                <div className="mt-auto space-y-4">
                    {/* Progress / current support */}
                    <div>
                        {hasGoal ? (
                            <>
                                <div className="flex items-baseline justify-between mb-2">
                                    <p className="font-heading text-2xl md:text-3xl text-[#1C252E] font-medium">
                                        ${Math.round(raised).toLocaleString()}
                                        <span className="text-sm text-[#4A5568] font-normal"> of ${Math.round(goal).toLocaleString()}</span>
                                    </p>
                                    <p className="text-sm font-bold text-[#E53935]" data-testid={`campaign-percent-${campaign.id}`}>
                                        {pct.toFixed(0)}%
                                    </p>
                                </div>
                                <div className="h-3 w-full bg-[#F4F1EA] rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-gradient-to-r from-[#E53935] to-[#D32F2F] rounded-full transition-all duration-700 ease-out"
                                        style={{ width: `${pct}%` }}
                                        data-testid={`campaign-bar-${campaign.id}`}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="flex items-baseline justify-between mb-2">
                                    <p className="font-heading text-2xl md:text-3xl text-[#1C252E] font-medium" data-testid={`campaign-current-${campaign.id}`}>
                                        ${Math.round(raised).toLocaleString()}
                                    </p>
                                    <p className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">
                                        Flexible fund
                                    </p>
                                </div>
                                <p className="text-sm text-[#4A5568] leading-relaxed">
                                    Available for approved needs — no fixed goal.
                                </p>
                            </>
                        )}
                        <p className="text-xs text-[#4A5568] mt-2 flex items-center gap-1.5">
                            <Users className="w-3 h-3" strokeWidth={1.5} />
                            {campaign.donors} {campaign.donors === 1 ? "donor" : "donors"} so far
                        </p>
                    </div>

                    <div className="flex gap-2 flex-wrap">
                        <Link
                            to={donateLink}
                            data-testid={`campaign-donate-link-${campaign.id}`}
                            className="inline-flex items-center justify-center gap-2 bg-[#E53935] text-white hover:bg-[#D32F2F] rounded-full px-6 py-3 text-sm font-medium transition-all hover:-translate-y-0.5 w-fit"
                        >
                            Give to this campaign
                            <ArrowRight className="w-4 h-4" strokeWidth={2} />
                        </Link>
                        {campaign.slug && (
                            <Link
                                to={landingLink}
                                data-testid={`campaign-view-link-${campaign.id}`}
                                className="inline-flex items-center justify-center gap-2 bg-transparent border border-black/10 text-[#1C252E] hover:border-[#E53935] hover:text-[#E53935] rounded-full px-5 py-3 text-sm font-medium transition-all"
                            >
                                View campaign
                            </Link>
                        )}
                        <button
                            onClick={handleShare}
                            data-testid={`campaign-share-${campaign.id}`}
                            className="inline-flex items-center justify-center gap-2 bg-transparent border border-black/10 text-[#1C252E] hover:border-[#E53935] hover:text-[#E53935] rounded-full px-5 py-3 text-sm font-medium transition-all"
                        >
                            <Share2 className="w-4 h-4" strokeWidth={1.5} />
                            Share
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CampaignThermometer;
